import { fileWithResolvedMimeType } from '@affine/core/utils/blob-file-type';
import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import { workspaceBlobQuotaQuery } from '@affine/graphql';
import {
  resolveCloudHttpBaseUrl,
  resolveCloudHttpEndpoint,
} from '@affine/nbstore/cloud';
import type { UploadProgressCallback } from '@blocksuite/affine/shared/services';
import { streamingSha256 } from '@blocksuite/affine/shared/utils';
import * as tus from 'tus-js-client';

import { workspaceApiFetch } from '../../cloud';
import {
  workspaceBlobCacheToken,
  WorkspaceBlobLocalStorageUnavailableError,
} from './blob-cache';

const TUS_CONFIG_CACHE_MS = 120_000;

interface TusUploadConfig {
  enabled: boolean;
  path: string | null;
}

interface CachedTusUploadConfig {
  config: TusUploadConfig;
  expiresAt: number;
}

export interface UploadWorkspaceFileViaTusOptions {
  workspaceId: string;
  file: File;
  expectedBlobKey?: string;
  docId?: string;
  onProgress?: UploadProgressCallback;
  serverBaseUrl?: string;
}

const tusConfigCache = new Map<string, CachedTusUploadConfig>();

export class WorkspaceBlobHashMismatchError extends Error {}

export function isWorkspaceBlobHashMismatchError(
  error: unknown
): error is WorkspaceBlobHashMismatchError {
  return error instanceof WorkspaceBlobHashMismatchError;
}

function normalizeServerBaseUrl(serverBaseUrl?: string): string {
  return resolveCloudHttpBaseUrl(serverBaseUrl ?? location.origin);
}

async function getWorkspaceTusConfig(
  workspaceId: string,
  serverBaseUrl: string
): Promise<TusUploadConfig> {
  const cacheKey = `${serverBaseUrl}:${workspaceId}`;
  const cached = tusConfigCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.config;
  }

  // Discovery failures deliberately bubble without populating the cache.
  // Reconnect retries must discover the current server capability immediately.
  const response = await workspaceApiFetch(
    resolveCloudHttpEndpoint(serverBaseUrl, '/graphql'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        query: workspaceBlobQuotaQuery.query,
        variables: { id: workspaceId },
      }),
    },
    { workspaceId, category: 'workspace-api' }
  );
  const json = await response.json();
  const quota = json.data?.workspace?.quota;
  const config = {
    enabled: quota?.tusUploadEnabled ?? false,
    path: quota?.tusUploadPath ?? null,
  };
  tusConfigCache.set(cacheKey, {
    config,
    expiresAt: Date.now() + TUS_CONFIG_CACHE_MS,
  });
  return config;
}

function uploadViaTus({
  workspaceId,
  file,
  expectedBlobKey,
  tusPath,
  serverBaseUrl,
  onProgress,
  docId,
}: UploadWorkspaceFileViaTusOptions & {
  tusPath: string;
  serverBaseUrl: string;
}): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let blobKey: string | null = null;

    const upload = new tus.Upload(file, {
      endpoint: resolveCloudHttpEndpoint(serverBaseUrl, tusPath),
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata: {
        workspaceId,
        uploadMode: 'content-addressed',
        mimeType: file.type || 'application/octet-stream',
        ...(docId && { docId }),
      },
      onBeforeRequest: req => {
        const xhr = req.getUnderlyingObject();
        if (xhr && 'withCredentials' in xhr) {
          (xhr as XMLHttpRequest).withCredentials = true;
        }
      },
      onAfterResponse: (_req, res) => {
        const key = res.getHeader('X-Blob-Key');
        if (key) {
          blobKey = key;
        }
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress?.(bytesUploaded, bytesTotal);
      },
      onError: error => reject(error),
      onSuccess: () => {
        if (!blobKey) {
          reject(new Error('Server did not return X-Blob-Key header'));
          return;
        }
        if (expectedBlobKey && blobKey !== expectedBlobKey) {
          reject(
            new WorkspaceBlobHashMismatchError(
              `TUS content-addressed upload returned mismatched blob key ${blobKey}`
            )
          );
          return;
        }
        resolve(blobKey);
      },
    });

    upload.start();
  });
}

async function uploadWorkspaceFileViaTusInternal(
  {
    workspaceId,
    file,
    expectedBlobKey,
    docId,
    onProgress,
    serverBaseUrl,
  }: UploadWorkspaceFileViaTusOptions,
  reuseStagedCache: boolean
): Promise<string | null> {
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  const normalizedServerBaseUrl = normalizeServerBaseUrl(serverBaseUrl);
  const config = await getWorkspaceTusConfig(
    workspaceId,
    normalizedServerBaseUrl
  );
  if (!config.enabled || !config.path) return null;

  const uploadFile = await fileWithResolvedMimeType(file);
  const blobKey = await uploadViaTus({
    workspaceId,
    file: uploadFile,
    expectedBlobKey,
    docId,
    onProgress,
    tusPath: config.path,
    serverBaseUrl: normalizedServerBaseUrl,
  });

  const cached = reuseStagedCache
    ? await opfsBlobCache.setUploadState(workspaceId, blobKey, 'synced')
    : await opfsBlobCache.set(workspaceId, blobKey, uploadFile, {
        mime: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
        source: 'upload',
        cacheToken: workspaceBlobCacheToken(blobKey, uploadFile.size),
        uploadState: 'synced',
      });
  if (!cached) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  return blobKey;
}

export async function uploadWorkspaceFileViaTus(
  options: UploadWorkspaceFileViaTusOptions
): Promise<string | null> {
  return uploadWorkspaceFileViaTusInternal(options, false);
}

export interface StageWorkspaceFileOptions {
  workspaceId: string;
  file: File;
}

/**
 * Persist upload bytes in the canonical OPFS cache under their content hash.
 * This is the sole durable binary copy used by offline rendering and retries.
 */
export async function stageWorkspaceFile({
  workspaceId,
  file,
}: StageWorkspaceFileOptions): Promise<{ blobKey: string; file: File }> {
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  const uploadFile = await fileWithResolvedMimeType(file);
  const blobKey = await streamingSha256(uploadFile);
  const cached = await opfsBlobCache.set(workspaceId, blobKey, uploadFile, {
    mime: uploadFile.type || 'application/octet-stream',
    size: uploadFile.size,
    source: 'upload',
    cacheToken: workspaceBlobCacheToken(blobKey, uploadFile.size),
    uploadState: 'pending',
  });
  if (!cached) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }

  return { blobKey, file: uploadFile };
}

export async function uploadStagedWorkspaceFileViaTus(
  options: Omit<
    UploadWorkspaceFileViaTusOptions,
    'file' | 'expectedBlobKey'
  > & {
    blobKey: string;
  }
): Promise<string | null> {
  const file = await opfsBlobCache.getCompleteFile(
    options.workspaceId,
    options.blobKey
  );
  if (!file) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }
  const { blobKey, ...uploadOptions } = options;
  return uploadWorkspaceFileViaTusInternal(
    {
      ...uploadOptions,
      file,
      expectedBlobKey: blobKey,
    },
    true
  );
}
